import type { ApplicationBackofficeMenuOption } from '@solfacil/girassol/dist/types/components/headers/header-backoffice/applications/menu/types';
import type { SidebarBackofficeItemMenu } from '@solfacil/girassol/dist/types/components/sidebars/sidebar-backoffice/types';
import { jwtDecode } from 'jwt-decode';
import type { ComputedOptions, ConcreteComponent, MethodOptions } from 'vue';

import IconActivity from '~icons/girassol/activity';
import IconCollectionsBookmark from '~icons/girassol/collections-bookmark';
import IconDescription from '~icons/girassol/description';
import IconEnergyMoney from '~icons/girassol/energy-consumption-money';
import IconLibraryBooks from '~icons/girassol/library-books';
import IconShipping from '~icons/girassol/local-shipping';
import IconSell from '~icons/girassol/sell';
import IconShoppingCart from '~icons/girassol/shopping-cart';
import IconSolarPower from '~icons/girassol/solar-power';
import IconSolfacil from '~icons/girassol/solfacil';


type ResourceAccess = {
  [client: string]: {
    roles: string[];
  };
};

export function useApplication (linkResolver: ConcreteComponent<{}, any, any, ComputedOptions, MethodOptions, {}, any> | string) {
  const accessToken = useCookie('access_token');
  const decoded = jwtDecode(accessToken.value || '') as any;

  const roles = Object.entries(decoded.resource_access as ResourceAccess).reduce<string[]>((acc, [client, resource]) => {
    const modifiedRoles = resource.roles.map((role: string) => `${client}#${role}`);
    return [...acc, ...modifiedRoles];
  }, []);

  const adminPermission = decoded.groups.some((group: string) => group.includes('admin') || group.includes('full_access'));
  const pricingPermission = roles.includes('ecommerce#pricing:read');
  const logisticPermission = roles.includes('ecommerce#logistic:read');
  const planningPermission = roles.includes('ecommerce#planning:read');
  const couponPermission = roles.includes('ecommerce#coupon:read');
  const auditPermission = roles.includes('ecommerce#audit:read');

  if (!adminPermission) {
    showError({
      message: 'Você não tem permissão pra acessar essa página',
      statusCode: 401
    });
  }

  const applications = ref<ApplicationBackofficeMenuOption[]>([
    {
      id: 'backoffice-ecommerce',
      icon: IconShoppingCart,
      label: 'Backoffice Loja Solfácil',
      current: true,
      tag: 'a'
    },
    {
      id: 'backoffice-solarinove',
      icon: IconSolarPower,
      label: ' Backoffice Solar Inove',
      current: false,
      tag: 'a',
      action: () => {
        window.open('https://app.solarinove.com.br/', '_blank');
      }
    },
    {
      id: 'backoffice-solfacil',
      icon: IconSolfacil,
      label: ' Backoffice Solfácil',
      current: false,
      tag: 'a',
      action: () => {
        window.open('https://backoffice.solfacil.com.br/home', '_blank');
      }
    }
  ]);

  const menus = ref<SidebarBackofficeItemMenu[]>([]);

  if (adminPermission) {
    menus.value.push({
      id: 'orcamentos',
      icon: IconCollectionsBookmark,
      label: 'Orçamentos',
      tag: linkResolver,
      link: '/orcamentos',
      linkProp: 'to'
    },
    {
      id: 'pedidos',
      icon: IconLibraryBooks,
      label: 'Pedidos',
      tag: linkResolver,
      link: '/pedidos',
      linkProp: 'to'
    }
    );
  }
  if (couponPermission) {
    menus.value.push({
      id: 'cupons',
      icon: IconSell,
      label: 'Cupons',
      tag: linkResolver,
      link: '/cupons',
      linkProp: 'to'
    });
  }
  if (pricingPermission) {
    menus.value.push({
      id: 'precificacao',
      icon: IconEnergyMoney,
      label: 'Precificação',
      tag: linkResolver,
      link: '/precificacao',
      linkProp: 'to'
    });
  }
  if (logisticPermission) {
    menus.value.push({
      id: 'logistica',
      icon: IconShipping,
      label: 'Logística',
      tag: linkResolver,
      link: '/logistica',
      linkProp: 'to'
    });
  }
  if (planningPermission) {
    menus.value.push({
      id: 'planejamento',
      icon: IconDescription,
      label: 'Planejamento',
      tag: linkResolver,
      link: '/planejamento',
      linkProp: 'to'
    });
  }
  if (auditPermission) {
    menus.value.push({
      id: 'auditoria',
      icon: IconActivity,
      label: 'Auditoria',
      tag: linkResolver,
      link: '/auditoria',
      linkProp: 'to'
    });
  }

  return {
    applications,
    menus
  };
}
